// Build the dashboard_data.json bundle from in-memory Transactions.
// Mirrors scripts/process_month.py::_summarize and the months[] layout
// the desktop dashboard consumes via invoke("load_bundle").
//
// Producing the same shape means we can later either:
//   - graft the existing src/index.html dashboard on top of a tauri-invoke
//     shim that returns this bundle, or
//   - render summary stats directly in the simpler web UI from the same
//     numbers (keeping the bundle as a single source of truth).

import type { Transaction } from "./common.js";
import { detectDuplicates, type ReconIssue } from "./reconcile.js";

export interface MonthBundle {
  month: string; // YYYY-MM
  transactions: Transaction[];
  files_seen: Array<{ file: string; portal: string }>;
  recon: { issues: ReconIssue[] };
  summary: MonthSummary;
}

export interface MonthSummary {
  income_total: number;
  expense_total: number;
  transaction_count: number;
  by_category: Record<string, number>;
  by_subcategory: Record<string, number>;
}

export interface Bundle {
  months: MonthBundle[];
  categories: unknown;
  generated_at: string;
}

// Mirrors _summarize in scripts/process_month.py.
// Sign convention: positive = expense, negative = income.
// Deleted transactions are excluded entirely.
export function summarize(txs: Transaction[]): MonthSummary {
  let income = 0;
  let expense = 0;
  const byCategory: Record<string, number> = {};
  const bySubcategory: Record<string, number> = {};
  let count = 0;
  for (const tx of txs) {
    if (tx.deleted) continue;
    count++;
    if (tx.amount < 0) {
      income += -tx.amount;
    } else {
      expense += tx.amount;
      byCategory[tx.category] = (byCategory[tx.category] ?? 0) + tx.amount;
      const subKey = `${tx.category} / ${tx.subcategory}`;
      bySubcategory[subKey] = (bySubcategory[subKey] ?? 0) + tx.amount;
    }
  }
  return {
    income_total: round2(income),
    expense_total: round2(expense),
    transaction_count: count,
    by_category: roundValues(byCategory),
    by_subcategory: roundValues(bySubcategory),
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
function roundValues(obj: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(obj)) out[k] = round2(v);
  return out;
}

// Given a flat list of transactions and the per-file outcomes, build the
// month-bucketed bundle. files_seen is computed per-month by matching
// each transaction's source_file against the perFile entries — every file
// that contributed at least one row to a given month appears under that
// month's files_seen. Best-effort attribution; the desktop pipeline gets
// per-month attribution for free because it reads from raw/<month>/
// directly. We'll match that fidelity when wiring up folder reads.
export function buildBundle(
  byMonth: Map<string, Transaction[]>,
  perFile: Array<{ filename: string; portal: string }>,
  categoriesDoc: unknown,
): Bundle {
  const months: MonthBundle[] = [];
  const sortedMonths = [...byMonth.keys()].sort();
  for (const month of sortedMonths) {
    const txs = byMonth.get(month) ?? [];
    const filesSeen = perFile
      .filter((f) => txs.some((t) => t.source_file === f.filename))
      .map((f) => ({ file: f.filename, portal: f.portal }));
    months.push({
      month,
      transactions: txs,
      files_seen: filesSeen,
      recon: { issues: detectDuplicates(txs) },
      summary: summarize(txs),
    });
  }
  return {
    months,
    categories: categoriesDoc,
    generated_at: new Date().toISOString(),
  };
}
